// charts/PieChart.jsx
import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * PieChart
 * --------
 * Minimal donut chart wrapper with optional center title + subtitle.
 *
 * Props:
 *   data:            Array<{ label: string, value: number, color?: string }>
 *   legend:          'right' | 'bottom' | null/undefined to hide
 *   title:           string | null
 *   dataLabels:      boolean
 *   innerSize:       string | number (e.g. '60%')
 *   centerLabel:     string | undefined     // large text in the donut hole
 *   centerSubLabel:  string | undefined     // smaller text beneath the centerLabel
 *   tooltipOptions:  object | undefined     // Highcharts tooltip options merged over the defaults
 *   onClick:         (dataPoint) => void
 */
export const PieChart = ({
  data = [],
  legend,
  title = null,
  dataLabels = true,
  innerSize = '60%',
  pieSize = '78%',
  centerLabel,
  centerSubLabel,
  tooltipOptions,
  onClick,
}) => {
  const theme = useTheme();
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;

  const seriesData = data.map((dataPoint) => ({
    id: dataPoint.label, // stable point id for smoother updates
    name: dataPoint.label,
    y: dataPoint.value,
    color: dataPoint.color,
    original: dataPoint,
  }));

  const legendOptions =
    legend == null ? { enabled: false }
    : legend === 'bottom' ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
    : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const options = {
    chart: {
      type: 'pie',
      spacing: [8, 8, 24, 8],
      animation: { duration: 300 },
      events:
        centerLabel || centerSubLabel ?
          {
            render: function () {
              const chart = this;
              const series = chart.series && chart.series[0];
              if (!series) return;

              const [centerX, centerY] = series.center || [0, 0];
              const x = chart.plotLeft + centerX;
              const y = chart.plotTop + centerY;

              // Create/update the center title
              if (centerLabel) {
                if (!chart.customCenterTitle) {
                  chart.customCenterTitle = chart.renderer
                    .text(centerLabel, x, y)
                    .attr({
                      align: 'center',
                      'text-anchor': 'middle',
                      zIndex: 5,
                    })
                    .css({
                      fontSize: '13px',
                      fontWeight: 500,
                      color: chart.theme?.textColor || '#000',
                      pointerEvents: 'none',
                    })
                    .add();
                } else {
                  chart.customCenterTitle.attr({ text: String(centerLabel) }).css({ color: textPrimary });
                }
              }

              // Create/update the center subtitle
              if (centerSubLabel) {
                if (!chart.customCenterSubtitle) {
                  chart.customCenterSubtitle = chart.renderer
                    .text(String(centerSubLabel))
                    .attr({
                      align: 'center',
                      zIndex: 5,
                      'text-anchor': 'middle',
                      'dominant-baseline': 'middle',
                    })
                    .css({
                      fontSize: '12px',
                      fontWeight: 400,
                      color: textSecondary,
                      pointerEvents: 'none',
                    })
                    .add();
                } else {
                  chart.customCenterSubtitle.attr({ text: String(centerSubLabel) }).css({ color: textSecondary });
                }
              } else if (chart.customCenterSubtitle) {
                chart.customCenterSubtitle.destroy();
                chart.customCenterSubtitle = null;
              }

              // Position: title centered; subtitle just below with dynamic gap
              const gap = 0; // px between title and subtitle

              if (chart.customCenterTitle) {
                let reneredCustomCenterTitle = chart.customCenterTitle;
                if (reneredCustomCenterTitle.element) {
                  const box = reneredCustomCenterTitle.getBBox();
                  const verticalAdjust = box.height * 0.25; // ~25% tweak
                  reneredCustomCenterTitle.attr({ x, y: y - box.height / 2 + verticalAdjust });
                }
              }

              if (chart.customCenterSubtitle) {
                const titleBox = chart.customCenterTitle?.getBBox?.() || { height: 0 };
                const subtitleBox = chart.customCenterSubtitle.getBBox();
                const subtitleY = y + titleBox.height / 2 + gap + subtitleBox.height / 2;
                chart.customCenterSubtitle.attr({ x, y: subtitleY });
              }
            },
          }
        : undefined,
    },
    title: { text: title },
    legend: legendOptions,
    // Default tooltip is fine for now
    plotOptions: {
      pie: {
        tooltip: {
          useHTML: true,
          pointFormatter: function () {
            let tooltip = `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${this.y}</b><br/>`;
            if (this.original.tooltipHtml) {
              tooltip += this.original.tooltipHtml;
            }
            return tooltip;
          },
          ...tooltipOptions,
        },
        innerSize, // donut thickness
        size: pieSize,
        borderRadius: 6,
        borderWidth: 0, // remove white ring in dark mode
        borderColor: 'transparent',
        states: {
          hover: { halo: { size: 0 }, brightness: 0 },
        },
        dataLabels: {
          enabled: dataLabels,
          distance: 16,
          padding: 2,
          softConnector: false,
          connectorPadding: 2,
          allowOverlap: true,
          style: { fontSize: '11px', fontWeight: 400, textOutline: 'none', width: 90 },
        },
        cursor: onClick ? 'pointer' : 'default',
        point:
          onClick ?
            {
              events: {
                click: function () {
                  onClick(this.options.original ?? this);
                },
              },
            }
          : {},
      },
    },
    series: [
      {
        id: 'pie', // stable series id for smooth updates
        name: 'Value',
        data: seriesData,
        showInLegend: !!legendOptions.enabled,
      },
    ],
  };

  return <BaseHighchart options={options} />;
};
